import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Info, Loader2, Search, Server } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { PasswordField } from "@/shared/components/ui/password-field";
import { useToast } from "@/shared/hooks/use-toast";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { IntegrationScopeField } from "./IntegrationScopeField";
import { useTitleAvailability } from "../hooks/useTitleAvailability";
import type { Integration, IntegrationBase, McpInspectResult } from "../types/integration";
import {
  DEFAULT_API_KEY_HEADER,
  apiKeyHeader,
  canContinueAfterInspect,
  mcpCredentials,
  needsToken,
  normalizeServerUrl,
  suggestedMcpTitle,
} from "../utils/mcpServer";
import { integrationPath } from "../utils/integrationTabs";

interface AddMcpServerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Template `mcp` escolhido no catálogo: a URL vem preenchida e travada. Ausente é o
   * "Adicionar novo" — a pessoa digita a URL de um servidor que o catálogo ainda não tem.
   */
  template?: Integration | null;
  /** Organização dona, quando NÃO é a ativa da sessão (AB#85605). */
  organizationId?: string;
  /**
   * O que fazer com a instância criada (sem OAuth). Padrão: abrir a página dela. Quem
   * instala de dentro de outro fluxo (o seletor de integrações de um agente) só recarrega.
   */
  onCreated?: (integration: IntegrationBase) => void;
}

type Step = "url" | "details";

/**
 * Instalar um servidor MCP remoto (AB#87477).
 *
 * Etapa 1, a URL: o `inspect` conecta sem gravar e diz como o servidor autentica. Com token,
 * a pessoa informa e inspeciona de novo para ver as tools; com OAuth, ela é avisada de que
 * vai autorizar no provedor. Se a URL já tem template no catálogo, a instalação passa a
 * ser desse template — a api reaproveita pela `server_url`.
 *
 * Etapa 2, a instalação: title (único na organização), escopo e as tools que vêm junto.
 */
export const AddMcpServerDialog = ({
  isOpen,
  onOpenChange,
  template,
  organizationId,
  onCreated,
}: AddMcpServerDialogProps) => {
  const { t } = useTranslation();
  const fieldId = useId();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();
  const { scope: viewScope, organizationId: viewOrganizationId } = useViewScope();
  // Mesma regra do IntegrationsForm: sem produto possível, o escopo é global e o campo some.
  const isOrgView = viewScope === "organization" || !!organizationId;
  const effectiveOrgId = organizationId || currentWorkspace?.organization_id || viewOrganizationId || null;

  const lockedUrl = template?.server_url || template?.mcp_config?.server_url || "";

  const [step, setStep] = useState<Step>("url");
  const [serverUrl, setServerUrl] = useState("");
  const [inspection, setInspection] = useState<McpInspectResult | null>(null);
  const [isInspecting, setIsInspecting] = useState(false);
  const [token, setToken] = useState("");
  const [authHeader, setAuthHeader] = useState(DEFAULT_API_KEY_HEADER);
  const [title, setTitle] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleAvailability = useTitleAvailability(undefined, organizationId);
  const resetTitleAvailability = titleAvailability.reset;
  const templateAuthHeader = template?.mcp_config?.auth_header;

  // Cada abertura começa do zero — SEGURANÇA: o token não sobrevive ao fechar.
  useEffect(() => {
    if (!isOpen) return;
    setStep("url");
    setServerUrl(lockedUrl);
    setInspection(null);
    setToken("");
    setAuthHeader(templateAuthHeader || DEFAULT_API_KEY_HEADER);
    setTitle("");
    setIsGlobal(isOrgView);
    resetTitleAvailability();
  }, [isOpen, lockedUrl, templateAuthHeader, isOrgView, resetTitleAvailability]);

  useEffect(() => {
    if (isOrgView && !isGlobal) setIsGlobal(true);
  }, [isOrgView, isGlobal]);

  const authMode = inspection?.auth.mode ?? template?.auth_mode ?? "none";
  const tools = useMemo(() => inspection?.tools ?? [], [inspection]);
  // O template do catálogo: o que veio do clique, ou o que o inspect achou pela URL.
  const catalogTemplate = template
    ? { id: template.id, label: template.label as string | null, logo_url: template.logo_url }
    : inspection?.existing_template ?? null;
  const logoUrl = sameOriginProxyUrl(catalogTemplate?.logo_url ?? null);

  const errorToast = (error: unknown) =>
    toast({
      title: t("common.error"),
      description: resolveApiErrorMessage(error, t),
      variant: "destructive",
    });

  const handleInspect = async () => {
    const url = normalizeServerUrl(serverUrl);
    if (!url) {
      toast({ title: t("common.error"), description: t("integrations.mcp.add.invalidUrl"), variant: "destructive" });
      return;
    }
    setIsInspecting(true);
    try {
      // Segunda inspeção: com a credencial informada, para listar as tools.
      const mode = inspection?.auth.mode;
      const credentials = mode ? mcpCredentials(mode, token) : undefined;
      const result = await IntegrationBaseService.inspectMcpServer(
        {
          server_url: url,
          ...(mode && credentials
            ? {
                auth_mode: mode,
                credentials,
                ...(mode === "apikey" ? { auth_header: apiKeyHeader(authHeader) } : {}),
              }
            : {}),
        },
        organizationId,
      );
      setInspection(result);
      setServerUrl(url);
      if (!title.trim()) setTitle(suggestedMcpTitle(result));
    } catch (error) {
      errorToast(error);
    } finally {
      setIsInspecting(false);
    }
  };

  const handleUrlChange = (value: string) => {
    setServerUrl(value);
    // URL nova, servidor novo: o que o inspect anterior disse não vale mais.
    if (inspection) {
      setInspection(null);
      setToken("");
      setTitle("");
    }
  };

  const handleSubmit = async () => {
    if (!inspection || isSubmitting) return;
    if (!effectiveOrgId) {
      toast({ title: t("common.error"), description: t("integrations.scope.missingOrg"), variant: "destructive" });
      return;
    }
    if (!isGlobal && !currentWorkspace) {
      toast({ title: t("common.error"), description: t("integrations.scope.missingWorkspace"), variant: "destructive" });
      return;
    }
    if (!title.trim()) {
      toast({ title: t("common.error"), description: t("integrations.form.title.required"), variant: "destructive" });
      return;
    }
    if (titleAvailability.status === "taken") {
      toast({ title: t("common.error"), description: t("integrations.form.title.taken"), variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const credentials = mcpCredentials(authMode, token);
      const result = await IntegrationBaseService.addMcpServer(
        {
          server_url: inspection.server_url || serverUrl,
          title: title.trim(),
          label: catalogTemplate?.label || inspection.server_info?.title || inspection.server_info?.name || undefined,
          scope: isGlobal ? "global" : "local",
          // product_id só no escopo do workspace — é ele que cria o vínculo (parity legacy).
          product_id: !isGlobal && currentWorkspace ? currentWorkspace.id : undefined,
          auth_mode: authMode,
          ...(credentials ? { credentials } : {}),
          ...(authMode === "apikey" ? { auth_header: apiKeyHeader(authHeader) } : {}),
        },
        organizationId,
      );
      const created = result.integration;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["integrations-base", effectiveOrgId] }),
        // Servidor novo vira template (pendente de revisão) — o catálogo muda junto.
        queryClient.invalidateQueries({ queryKey: ["integration-templates"] }),
      ]);
      window.dispatchEvent(new Event("tour-integration-created"));

      if (result.oauth_required) {
        // A instância nasce `pending`; o callback volta para a aba Conexão dela.
        try {
          const authorizationUrl = await IntegrationBaseService.startMcpOAuth(created.id, organizationId);
          window.location.assign(authorizationUrl);
        } catch (error) {
          // A instalação já existe: ficar no diálogo levaria a pessoa a tentar de novo e esbarrar
          // no nome já usado. Ela vai para a página, onde o botão Reconectar retoma a autorização.
          onOpenChange(false);
          navigate(integrationPath(created.id, "connection"));
          toast({
            title: t("integrations.mcp.add.oauthStartFailed"),
            description: resolveApiErrorMessage(error, t),
            variant: "destructive",
          });
        }
        return;
      }

      onOpenChange(false);
      if (onCreated) onCreated(created);
      else navigate(integrationPath(created.id));
    } catch (error) {
      errorToast(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canContinue = canContinueAfterInspect(inspection, token);
  const showTokenField = !!inspection && needsToken(inspection.auth.mode);

  const sortedTools = useMemo(() => [...tools].sort((a, b) => a.name.localeCompare(b.name)), [tools]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              {logoUrl ? (
                <img src={logoUrl} alt={catalogTemplate?.label ?? ""} className="h-full w-full object-contain p-1" />
              ) : (
                <Server className="h-5 w-5 text-primary" />
              )}
            </div>
            <div>
              <DialogTitle className="text-left">
                {template ? template.label : t("integrations.mcp.add.title")}
              </DialogTitle>
              <DialogDescription className="text-left text-sm">
                {step === "url" ? t("integrations.mcp.add.urlStep") : t("integrations.mcp.add.detailsStep")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="scroll-slim min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {step === "url" ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor={`${fieldId}-url`}>
                  {t("integrations.mcp.serverUrl")}
                </label>
                <div className="flex gap-2">
                  <Input
                    id={`${fieldId}-url`}
                    placeholder="https://mcp.example.com/mcp"
                    value={serverUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !inspection) void handleInspect();
                    }}
                    readOnly={!!lockedUrl}
                    className={lockedUrl ? "bg-muted font-mono text-xs" : "font-mono text-xs"}
                  />
                  {!inspection && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleInspect()}
                      disabled={isInspecting || !serverUrl.trim()}
                      className="shrink-0 gap-1"
                    >
                      {isInspecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      {t("integrations.mcp.add.inspect")}
                    </Button>
                  )}
                </div>
                {!inspection && <p className="text-xs text-muted-foreground">{t("integrations.mcp.add.urlHint")}</p>}
              </div>

              {/* A URL já tem template no catálogo: instalar ESTE, e não criar outro. */}
              {!template && inspection?.existing_template && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {t("integrations.mcp.add.alreadyInCatalog", { name: inspection.existing_template.label ?? inspection.server_url })}
                  </AlertDescription>
                </Alert>
              )}

              {inspection && (
                <div className="space-y-3 rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{t("integrations.mcp.authMode")}:</span>
                    <Badge variant="secondary">{t(`integrations.mcp.authModes.${inspection.auth.mode}`)}</Badge>
                    {inspection.server_info?.version && (
                      <span className="text-xs text-muted-foreground">v{inspection.server_info.version}</span>
                    )}
                  </div>

                  {inspection.auth.mode === "oauth" && (
                    <p className="text-xs text-muted-foreground">{t("integrations.mcp.add.oauthHint")}</p>
                  )}

                  {showTokenField && (
                    <div className="space-y-3">
                      {inspection.auth.mode === "apikey" && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor={`${fieldId}-header`}>
                            {t("integrations.mcp.authHeader")}
                          </label>
                          <Input
                            id={`${fieldId}-header`}
                            value={authHeader}
                            onChange={(e) => setAuthHeader(e.target.value)}
                            className="font-mono text-xs"
                          />
                        </div>
                      )}
                      <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor={`${fieldId}-token`}>
                          {inspection.auth.mode === "apikey" ? t("integrations.mcp.apiKey") : t("integrations.mcp.token")}
                          {inspection.auth.required && <span className="ml-1 text-destructive">*</span>}
                        </label>
                        <PasswordField
                          id={`${fieldId}-token`}
                          field={{ name: "mcp_token", label: t("integrations.mcp.token"), type: "password" }}
                          value={token}
                          onChange={setToken}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={() => void handleInspect()}
                        disabled={isInspecting || !token.trim()}
                      >
                        {isInspecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        {t("integrations.mcp.add.previewTools")}
                      </Button>
                    </div>
                  )}

                  {tools.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t("integrations.tools.count", { count: tools.length })}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor={`${fieldId}-title`}>
                  {t("integrations.form.title.label")}
                  <span className="ml-1 text-destructive">*</span>
                </label>
                <Input
                  id={`${fieldId}-title`}
                  placeholder={t("integrations.form.title.placeholder")}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    titleAvailability.reset();
                  }}
                  onBlur={() => titleAvailability.check(title)}
                  aria-invalid={titleAvailability.status === "taken"}
                />
                {titleAvailability.status === "taken" && (
                  <p className="text-xs text-destructive">{t("integrations.form.title.taken")}</p>
                )}
              </div>

              {!isOrgView && <IntegrationScopeField isGlobal={isGlobal} onChange={setIsGlobal} name="mcp-scope" />}

              <div className="space-y-2 border-t pt-2">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {t("integrations.tools.title")}
                  {tools.length > 0 && <span className="ml-1 font-normal">({tools.length})</span>}
                </h3>
                {sortedTools.length > 0 ? (
                  <ul className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {sortedTools.map((tool) => (
                      <li key={tool.name} className="rounded-md border px-3 py-2">
                        <p className="font-mono text-xs font-medium">{tool.name}</p>
                        {tool.description && (
                          <p className="line-clamp-2 text-xs text-muted-foreground">{tool.description}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {authMode === "oauth" ? t("integrations.mcp.add.toolsAfterOauth") : t("integrations.tools.empty")}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2 pt-4">
          {step === "details" ? (
            <Button variant="outline" onClick={() => setStep("url")} disabled={isSubmitting} className="gap-1">
              <ArrowLeft className="h-4 w-4" />
              {t("common.actions.back")}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              {t("integrations.actions.cancel")}
            </Button>
          )}
          {step === "url" ? (
            <Button className="flex-1" onClick={() => setStep("details")} disabled={!canContinue || isInspecting}>
              {t("common.actions.next")}
            </Button>
          ) : (
            <Button className="flex-1" onClick={() => void handleSubmit()} disabled={isSubmitting || !title.trim()}>
              {isSubmitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {authMode === "oauth" ? t("integrations.mcp.add.connectOauth") : t("integrations.actions.connect")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
